/**
 * Input: absolute touch/pointer mapping (finger x → track x — precise for 1D play) + relative
 * keyboard (A/D, arrows). One axis is the whole game (plan 01).
 */
export class InputController {
  private readonly targetX = { value: 0 };
  /** Held steering keys, per physical key (audit #3): a keyup can only release ITS own key, so
   *  a tap on any other key — same side or opposite — never kills a still-held one. */
  private readonly held = new Set<string>();
  private keyDir = 0;
  private pointerActive = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    canvas.addEventListener('pointerdown', this.onDown.bind(this), { passive: false });
    canvas.addEventListener('pointermove', this.onMove.bind(this), { passive: false });
    window.addEventListener('pointerup', this.onUp.bind(this));
    window.addEventListener('pointercancel', this.onUp.bind(this));
    window.addEventListener('keydown', this.onKey.bind(this));
    window.addEventListener('keyup', this.onKey.bind(this));
  }

  private onDown(event: PointerEvent): void {
    event.preventDefault();
    this.pointerActive = true;
    this.mapPointer(event);
  }

  private onMove(event: PointerEvent): void {
    if (!this.pointerActive) return;
    event.preventDefault();
    this.mapPointer(event);
  }

  private onUp(): void {
    this.pointerActive = false;
  }

  private mapPointer(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const t = (event.clientX - rect.left) / rect.width; // 0..1
    this.targetX.value = Math.max(-1, Math.min(1, (t - 0.5) * 2 * 1.15)); // slight gain
  }

  private onKey(event: KeyboardEvent): void {
    // Shift/caps can flip a letter's case between its keydown and keyup — normalize so the pair matches.
    const key = event.key === 'A' ? 'a' : event.key === 'D' ? 'd' : event.key;
    const left = key === 'ArrowLeft' || key === 'a';
    const right = key === 'ArrowRight' || key === 'd';
    if (!left && !right) return;
    event.preventDefault();
    if (event.type === 'keydown') this.held.add(key); // key-repeat re-adds — idempotent
    else this.held.delete(key);
    this.keyDir =
      ((this.held.has('ArrowRight') || this.held.has('d')) ? 1 : 0) -
      ((this.held.has('ArrowLeft') || this.held.has('a')) ? 1 : 0);
  }

  /** Current desired lateral target in [-1, 1] (call once per frame). */
  poll(dt: number): number {
    if (this.keyDir !== 0) {
      this.targetX.value = Math.max(-1, Math.min(1, this.targetX.value + this.keyDir * 2.6 * dt));
    }
    return this.targetX.value;
  }
}
